import React, { useState, useEffect, useMemo } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { EFilingParty } from '@lds-efsp/shared-contracts';
import FormInput, { FormSelect, FormCheckbox } from '../../setup/components/FormInput';
import {
  PARTY_ROLES,
  isRoleSelfRepEligible,
  ORGANIZATION_TYPES,
  ALTERNATE_NAME_TYPES,
  STREET_SUFFIXES,
} from '@lds-efsp/shared-contracts';
import { FormData } from '../../../shared/data/formData';
import AddressFields from '../../../shared/components/AddressFields';
import { ChoiceGroup } from '../../../shared/components/ui/ChoiceGroup';
import { useApi, API_BASE_URL } from '../../../api/useApi';

interface PartyRoleOption {
  code: string;
  name: string;
  selfRepVisible: boolean;
}

interface AddPartyModalProps {
  brand: BrandConfig;
  county: string;
  caseCategoryCode?: string;
  onSave: (party: EFilingParty) => void;
  onClose: () => void;
  existingParty?: EFilingParty;
  /**
   * Render the general Mailing Address block on the party record (Walter
   * R103 / WOL-447 — UD filings must capture the defendant's address;
   * LegalConnect's Edit-Party contact fields are the reference). Threaded
   * from the confirm flow only, so the classic modal is unchanged while the
   * 7/16 surface is frozen. When the self-rep contact block is showing, the
   * address is collected there instead — the two are never shown together.
   */
  showMailingAddress?: boolean;
}

const suffixOptions = [
  { value: '', label: 'Select...' },
  { value: 'Jr.', label: 'Jr.' },
  { value: 'Sr.', label: 'Sr.' },
  { value: 'II', label: 'II' },
  { value: 'III', label: 'III' },
  { value: 'IV', label: 'IV' },
];

const orgTypeOptions = [
  { value: '', label: 'Select type...' },
  ...ORGANIZATION_TYPES.map(o => ({ value: o.code, label: o.name })),
];

const altNameTypeOptions = [
  { value: '', label: 'Select type...' },
  ...ALTERNATE_NAME_TYPES.map(a => ({ value: a.code, label: a.name })),
];

const streetSuffixOptions = [
  { value: '', label: 'Select...' },
  ...STREET_SUFFIXES.map(s => ({ value: s.code, label: s.name })),
];

interface LanguageOption {
  code: string;
  name: string;
}

const AddPartyModal: React.FC<AddPartyModalProps> = ({
  brand,
  county,
  caseCategoryCode,
  onSave,
  onClose,
  existingParty,
  showMailingAddress = false,
}) => {
  const isEditing = !!existingParty;
  const { fetchWithAuth } = useApi();

  // Fetch party roles from policy — filtered by case category when available
  const [partyRoles, setPartyRoles] = useState<PartyRoleOption[]>(PARTY_ROLES);

  // WOL-340: interpreter languages must be the court's NUMERIC LANGUAGE codes,
  // not ISO 639 strings. Fetch them from policy per county so the value we
  // persist + send is one the EFM accepts. Empty until fetched (no ISO fallback
  // on the client — an ISO code would be silently rejected by the court).
  const [languages, setLanguages] = useState<LanguageOption[]>([]);

  useEffect(() => {
    if (!county) return;
    fetchWithAuth(`${API_BASE_URL}/api/efiling/reference/languages?county=${encodeURIComponent(county)}`)
      .then(res => res.json())
      .then(json => {
        if (json.data && json.data.length > 0) setLanguages(json.data);
      })
      .catch(() => {
        // Leave the picker empty on error — better than offering ISO codes the
        // court will reject. The "Requires Interpreter" copy still makes the
        // requirement visible; the operator can retry.
      });
  }, [county, fetchWithAuth]);

  const languageOptions = useMemo(
    () => [
      { value: '', label: 'Select language...' },
      ...languages.map(l => ({ value: l.code, label: l.name })),
    ],
    [languages],
  );

  useEffect(() => {
    if (!county) return;

    // Try case-fields endpoint first (returns category-filtered roles)
    if (caseCategoryCode) {
      const params = new URLSearchParams({ county, categoryCode: caseCategoryCode });
      fetchWithAuth(`${API_BASE_URL}/api/efiling/reference/case-fields?${params}`)
        .then(res => res.json())
        .then(json => {
          if (json.partyRoles) {
            setPartyRoles(json.partyRoles.map((r: { code: string; name: string }) => ({
              code: r.code,
              name: r.name,
              selfRepVisible: isRoleSelfRepEligible(r.code),
            })));
          }
        })
        .catch(() => {
          // Keep default PARTY_ROLES when category-filtered fetch fails
        });
    } else {
      fetchPartyRolesFallback();
    }

    function fetchPartyRolesFallback() {
      fetchWithAuth(`${API_BASE_URL}/api/efiling/reference/party-roles?county=${encodeURIComponent(county)}`)
        .then(res => res.json())
        .then(json => {
          if (json.data && json.data.length > 0) {
            setPartyRoles(json.data);
          }
        })
        .catch(() => {
          // Keep default PARTY_ROLES on error
        });
    }
  }, [county, caseCategoryCode, fetchWithAuth]);

  const roleOptions = [
    { value: '', label: 'Select role...' },
    ...[...partyRoles]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(r => ({ value: r.code, label: r.name })),
  ];

  const [partyType, setPartyType] = useState<'person' | 'organization'>(
    existingParty?.partyType || 'person',
  );
  const [roleCode, setRoleCode] = useState(existingParty?.roleCode || '');
  const [firstName, setFirstName] = useState(existingParty?.firstName || '');
  const [middleName, setMiddleName] = useState(existingParty?.middleName || '');
  const [lastName, setLastName] = useState(existingParty?.lastName || '');
  const [suffix, setSuffix] = useState(existingParty?.suffix || '');
  const [organizationName, setOrganizationName] = useState(existingParty?.organizationName || '');
  const [organizationType, setOrganizationType] = useState(existingParty?.organizationType || '');
  const [guardianAdLitem, setGuardianAdLitem] = useState(
    existingParty?.partySubTypes?.includes('guardian-ad-litem') || false,
  );
  const [incompetentPerson, setIncompetentPerson] = useState(
    existingParty?.partySubTypes?.includes('incompetent-person') || false,
  );
  const [minor, setMinor] = useState(
    existingParty?.partySubTypes?.includes('minor') || false,
  );
  const [isLeadClient, setIsLeadClient] = useState(existingParty?.isLeadClient || false);
  const [billingZip, setBillingZip] = useState(existingParty?.billingZip || '');
  const [feeExemptionType, setFeeExemptionType] = useState<'NONE' | 'FEE_WAIVER' | 'GOVT_ENTITY'>(
    existingParty?.feeExemptionType || (existingParty?.requestFeesExemption ? 'FEE_WAIVER' : 'NONE'),
  );
  const [feeWaiverOnFile, setFeeWaiverOnFile] = useState(existingParty?.feeWaiverOnFile || false);
  const [eServiceConsent, setEServiceConsent] = useState(existingParty?.eServiceConsent || false);
  const [isSelfRepresented, setIsSelfRepresented] = useState(
    existingParty?.isSelfRepresented || false,
  );
  const [selfRepCountry, setSelfRepCountry] = useState(
    existingParty?.selfRepAddress?.country || 'US',
  );
  const [selfRepAddress1, setSelfRepAddress1] = useState(
    existingParty?.selfRepAddress?.address1 || '',
  );
  const [selfRepStreetSuffix, setSelfRepStreetSuffix] = useState(
    existingParty?.selfRepAddress?.streetSuffix || '',
  );
  const [selfRepCity, setSelfRepCity] = useState(existingParty?.selfRepAddress?.city || '');
  const [selfRepState, setSelfRepState] = useState(existingParty?.selfRepAddress?.state || '');
  const [selfRepZip, setSelfRepZip] = useState(existingParty?.selfRepAddress?.zip || '');
  const [selfRepPhone, setSelfRepPhone] = useState(existingParty?.selfRepPhone || '');
  const [selfRepEmail, setSelfRepEmail] = useState(existingParty?.selfRepEmail || '');
  // General mailing address on the party record (R103 / WOL-447)
  const [mailingCountry, setMailingCountry] = useState(
    existingParty?.mailingAddress?.country || 'US',
  );
  const [mailingAddress1, setMailingAddress1] = useState(
    existingParty?.mailingAddress?.address1 || '',
  );
  const [mailingStreetSuffix, setMailingStreetSuffix] = useState(
    existingParty?.mailingAddress?.streetSuffix || '',
  );
  const [mailingCity, setMailingCity] = useState(existingParty?.mailingAddress?.city || '');
  const [mailingState, setMailingState] = useState(existingParty?.mailingAddress?.state || '');
  const [mailingZip, setMailingZip] = useState(existingParty?.mailingAddress?.zip || '');
  const [hasAlternateNames, setHasAlternateNames] = useState(
    existingParty?.hasAlternateNames || false,
  );
  const [altNameType, setAltNameType] = useState('');
  const [altName, setAltName] = useState('');
  const [alternateNames, setAlternateNames] = useState(existingParty?.alternateNames || []);
  const [requiresInterpreter, setRequiresInterpreter] = useState(
    existingParty?.requiresInterpreter || false,
  );
  const [interpreterLanguage, setInterpreterLanguage] = useState(
    existingParty?.interpreterLanguage || '',
  );

  const selectedRole = partyRoles.find(r => r.code === roleCode);
  const showSelfRep = isRoleSelfRepEligible(roleCode);

  const handleAddAltName = () => {
    if (altNameType && altName) {
      setAlternateNames(prev => [
        ...prev,
        { type: altNameType as 'AKA' | 'Alias' | 'DBA' | 'ESA' | 'FDBA' | 'FKA', name: altName },
      ]);
      setAltNameType('');
      setAltName('');
    }
  };

  const handleRemoveAltName = (index: number) => {
    setAlternateNames(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const partySubTypes: ('guardian-ad-litem' | 'incompetent-person' | 'minor')[] = [];
    if (guardianAdLitem) partySubTypes.push('guardian-ad-litem');
    if (incompetentPerson) partySubTypes.push('incompetent-person');
    if (minor) partySubTypes.push('minor');

    const party: EFilingParty = {
      id: existingParty?.id || crypto.randomUUID(),
      partyType,
      roleCode,
      roleName: selectedRole?.name || '',
      firstName: partyType === 'person' ? firstName : undefined,
      middleName: partyType === 'person' ? middleName : undefined,
      lastName: partyType === 'person' ? lastName : undefined,
      suffix: partyType === 'person' && suffix ? suffix as 'Jr.' | 'Sr.' | 'II' | 'III' | 'IV' : undefined,
      organizationName: partyType === 'organization' ? organizationName : undefined,
      organizationType: partyType === 'organization' && organizationType
        ? organizationType as 'ENTITY_OTHER' | 'GOVERNMENT_AGENCY' | 'TRUST'
        : undefined,
      partySubTypes: partyType === 'person' ? partySubTypes : [],
      isLeadClient,
      billingZip: isLeadClient ? billingZip : undefined,
      // Subsequent-filing flag — defaults false. Wired in a future UI pass
      // for amended-complaint NEW_PLAINTIFF / NEW_RESPONDING_PARTY emission.
      isNewToCase: existingParty?.isNewToCase ?? false,
      // Court-reported waiver (SOR-239) — carry the existing observation
      // through: the save path full-replaces the party, so dropping it here
      // would silently delete the read-only pill on the first Edit→Save.
      courtWaiver: existingParty?.courtWaiver,
      feeExemptionType,
      requestFeesExemption: feeExemptionType !== 'NONE',
      feeWaiverOnFile: partyType === 'person' && feeExemptionType === 'FEE_WAIVER' ? feeWaiverOnFile : false,
      eServiceConsent,
      isSelfRepresented: showSelfRep ? isSelfRepresented : false,
      selfRepAddress: isSelfRepresented && showSelfRep ? {
        country: selfRepCountry,
        address1: selfRepAddress1,
        streetSuffix: selfRepStreetSuffix,
        city: selfRepCity,
        state: selfRepState,
        zip: selfRepZip,
      } : undefined,
      selfRepPhone: isSelfRepresented && showSelfRep && selfRepPhone ? selfRepPhone : undefined,
      selfRepEmail: isSelfRepresented && showSelfRep && selfRepEmail ? selfRepEmail : undefined,
      // R103: only the confirm flow's modal collects this — when the block
      // isn't rendered, carry the existing value through untouched so a
      // classic-flow edit never wipes it.
      mailingAddress: showMailingAddress
        ? (mailingAddress1 || mailingCity || mailingState || mailingZip
          ? {
              country: mailingCountry,
              address1: mailingAddress1,
              streetSuffix: mailingStreetSuffix,
              city: mailingCity,
              state: mailingState,
              zip: mailingZip,
            }
          : undefined)
        : existingParty?.mailingAddress,
      hasAlternateNames,
      alternateNames: hasAlternateNames ? alternateNames : [],
      requiresInterpreter,
      interpreterLanguage: requiresInterpreter ? interpreterLanguage : undefined,
    };

    onSave(party);
  };

  const isValid = roleCode && (
    (partyType === 'person' && firstName && lastName) ||
    (partyType === 'organization' && organizationName)
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ borderColor: brand.colors.border }}
      >
        {/* Header */}
        <div
          className="flex justify-between items-center px-6 py-4 border-b"
          style={{ borderColor: brand.colors.border }}
        >
          <h3 className="text-lg font-semibold" style={{ color: brand.colors.text }}>
            {isEditing ? 'Edit Party' : 'Add Party'}
          </h3>
          <button
            onClick={onClose}
            className="text-xl font-light"
            style={{ color: brand.colors.textLight }}
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {/* Person / Organization toggle */}
          <ChoiceGroup question="Party type" hideLegend className="flex gap-4 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="partyType"
                checked={partyType === 'person'}
                onChange={() => setPartyType('person')}
                style={{ accentColor: brand.colors.primary }}
              />
              <span className="text-sm" style={{ color: brand.colors.text }}>Person</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="partyType"
                checked={partyType === 'organization'}
                onChange={() => setPartyType('organization')}
                style={{ accentColor: brand.colors.primary }}
              />
              <span className="text-sm" style={{ color: brand.colors.text }}>Organization</span>
            </label>
          </ChoiceGroup>

          {/* Role */}
          <FormSelect
            brand={brand}
            label="Role"
            name="roleCode"
            value={roleCode}
            onChange={e => setRoleCode(e.target.value)}
            options={roleOptions}
            required
          />

          {/* Person fields */}
          {partyType === 'person' && (
            <>
              {/* Party sub-types */}
              <div className="flex gap-4 flex-wrap mb-4">
                <FormCheckbox
                  brand={brand}
                  label="Guardian Ad Litem"
                  name="guardianAdLitem"
                  checked={guardianAdLitem}
                  onChange={e => setGuardianAdLitem(e.target.checked)}
                />
                <FormCheckbox
                  brand={brand}
                  label="Incompetent Person"
                  name="incompetentPerson"
                  checked={incompetentPerson}
                  onChange={e => setIncompetentPerson(e.target.checked)}
                />
                <FormCheckbox
                  brand={brand}
                  label="Minor"
                  name="minor"
                  checked={minor}
                  onChange={e => setMinor(e.target.checked)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  brand={brand}
                  label="First Name"
                  name="firstName"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  required
                />
                <FormInput
                  brand={brand}
                  label="Middle Name"
                  name="middleName"
                  value={middleName}
                  onChange={e => setMiddleName(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  brand={brand}
                  label="Last Name"
                  name="lastName"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  required
                />
                <FormSelect
                  brand={brand}
                  label="Suffix"
                  name="suffix"
                  value={suffix}
                  onChange={e => setSuffix(e.target.value)}
                  options={suffixOptions}
                />
              </div>
            </>
          )}

          {/* Organization fields */}
          {partyType === 'organization' && (
            <>
              <FormInput
                brand={brand}
                label="Organization Name"
                name="organizationName"
                value={organizationName}
                onChange={e => setOrganizationName(e.target.value)}
                required
              />
              <FormSelect
                brand={brand}
                label="Organization Type"
                name="organizationType"
                value={organizationType}
                onChange={e => setOrganizationType(e.target.value)}
                options={orgTypeOptions}
              />
            </>
          )}

          {/* Common fields */}
          <div className="border-t mt-4 pt-4" style={{ borderColor: brand.colors.border }}>
            <FormSelect
              brand={brand}
              label="Fee Exemption Type"
              name="feeExemptionType"
              value={feeExemptionType}
              onChange={e => setFeeExemptionType(e.target.value as 'NONE' | 'FEE_WAIVER' | 'GOVT_ENTITY')}
              options={[
                { value: 'NONE', label: 'None' },
                { value: 'FEE_WAIVER', label: 'Fee Waiver' },
                { value: 'GOVT_ENTITY', label: 'Government Entity' },
              ]}
            />

            {partyType === 'person' && feeExemptionType === 'FEE_WAIVER' && (
              <FormCheckbox
                brand={brand}
                label="Fee Waiver on File"
                name="feeWaiverOnFile"
                checked={feeWaiverOnFile}
                onChange={e => setFeeWaiverOnFile(e.target.checked)}
              />
            )}

            {/* Court-reported waiver status (SOR-239) — read-only observation
                beside the filer's own flag above, never a second control for
                it. Gated on the party carrying the court-returned group, not
                on the checkbox's person/FEE_WAIVER condition: the court's
                answer shows whenever it exists. */}
            {existingParty?.courtWaiver && (
              <div
                className="p-3 rounded border mb-4"
                style={{ borderColor: brand.colors.border }}
                data-testid="court-waiver-readout"
              >
                <p className="text-xs" style={{ color: brand.colors.textLight }}>
                  Court-reported status:{' '}
                  {existingParty.courtWaiver.efspFeeWaiverFiled === undefined
                    ? 'fee waiver filing not reported'
                    : existingParty.courtWaiver.efspFeeWaiverFiled
                      ? 'fee waiver filed with the court'
                      : 'no fee waiver filed with the court'}
                  {existingParty.courtWaiver.efmExemptionType && ` · exemption ${existingParty.courtWaiver.efmExemptionType}`}
                  {existingParty.courtWaiver.efmFeeExemptionType && ` · fee exemption ${existingParty.courtWaiver.efmFeeExemptionType}`}
                  . Set by the court record — read-only.
                </p>
              </div>
            )}

            {/* Alternate names */}
            <FormCheckbox
              brand={brand}
              label="Has Alternate Names"
              name="hasAlternateNames"
              checked={hasAlternateNames}
              onChange={e => setHasAlternateNames(e.target.checked)}
            />
            {hasAlternateNames && (
              <div className="ml-6 mb-4">
                <div className="flex gap-2 mb-2">
                  <FormSelect
                    brand={brand}
                    label="Type"
                    name="altNameType"
                    value={altNameType}
                    onChange={e => setAltNameType(e.target.value)}
                    options={altNameTypeOptions}
                  />
                  <FormInput
                    brand={brand}
                    label="Name"
                    name="altName"
                    value={altName}
                    onChange={e => setAltName(e.target.value)}
                  />
                  <button
                    onClick={handleAddAltName}
                    className="self-end mb-4 px-3 py-2 rounded text-sm"
                    style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
                  >
                    Add
                  </button>
                </div>
                {alternateNames.map((an, i) => (
                  <div key={`${an.type}-${an.name}`} className="flex justify-between items-center text-sm py-1">
                    <span style={{ color: brand.colors.text }}>
                      {an.type}: {an.name}
                    </span>
                    <button
                      onClick={() => handleRemoveAltName(i)}
                      className="text-red-500 text-xs"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Interpreter */}
            <FormCheckbox
              brand={brand}
              label="Requires an Interpreter"
              name="requiresInterpreter"
              checked={requiresInterpreter}
              onChange={e => setRequiresInterpreter(e.target.checked)}
            />
            {requiresInterpreter && (
              <div className="ml-6">
                <FormSelect
                  brand={brand}
                  label="Language"
                  name="interpreterLanguage"
                  value={interpreterLanguage}
                  onChange={e => setInterpreterLanguage(e.target.value)}
                  options={languageOptions}
                  required
                />
              </div>
            )}

            <FormCheckbox
              brand={brand}
              label="This party consents to electronic service (eService)"
              name="eServiceConsent"
              checked={eServiceConsent}
              onChange={e => setEServiceConsent(e.target.checked)}
            />

            {/* Self-Represented (only for plaintiff-side roles) */}
            {showSelfRep && (
              <>
                <FormCheckbox
                  brand={brand}
                  label="Is Self-Represented"
                  name="isSelfRepresented"
                  checked={isSelfRepresented}
                  onChange={e => setIsSelfRepresented(e.target.checked)}
                />
                {isSelfRepresented && (
                  <div className="ml-6 p-3 rounded border mb-4" style={{ borderColor: brand.colors.border }}>
                    <h4 className="text-sm font-medium mb-2" style={{ color: brand.colors.text }}>
                      Contact Information
                    </h4>
                    <AddressFields
                      brand={brand}
                      prefix="selfRep"
                      value={{
                        street1: selfRepAddress1,
                        streetSuffix: selfRepStreetSuffix,
                        city: selfRepCity,
                        state: selfRepState,
                        zip: selfRepZip,
                        country: selfRepCountry,
                      }}
                      onChange={addr => {
                        setSelfRepAddress1(addr.street1);
                        setSelfRepStreetSuffix(addr.streetSuffix ?? '');
                        setSelfRepCity(addr.city);
                        setSelfRepState(addr.state);
                        setSelfRepZip(addr.zip);
                        setSelfRepCountry(addr.country ?? '');
                      }}
                      required
                      showStreet2={false}
                      showStreetSuffix
                      streetSuffixOptions={streetSuffixOptions}
                      showCountry
                      statesOptions={FormData.states}
                      street1Label="Address"
                    />
                    <FormInput
                      brand={brand}
                      label="Phone"
                      name="selfRepPhone"
                      type="tel"
                      value={selfRepPhone}
                      onChange={e => setSelfRepPhone(e.target.value)}
                    />
                    <FormInput
                      brand={brand}
                      label="Email"
                      name="selfRepEmail"
                      type="email"
                      value={selfRepEmail}
                      onChange={e => setSelfRepEmail(e.target.value)}
                      required
                    />
                  </div>
                )}
              </>
            )}

            {/* Mailing Address (R103 / WOL-447 — confirm flow only). Hidden
                while the self-rep contact block above is collecting the same
                address, so the filer is never asked twice. */}
            {showMailingAddress && !(showSelfRep && isSelfRepresented) && (
              <div
                className="p-3 rounded border mb-4"
                style={{ borderColor: brand.colors.border }}
                data-testid="party-mailing-address"
              >
                <h4 className="text-sm font-medium mb-1" style={{ color: brand.colors.text }}>
                  Mailing Address
                </h4>
                <p className="text-xs mb-2" style={{ color: brand.colors.textLight }}>
                  The party&apos;s mailing address. Required for the defendant on
                  unlawful detainer filings.
                </p>
                <AddressFields
                  brand={brand}
                  prefix="mailing"
                  value={{
                    street1: mailingAddress1,
                    streetSuffix: mailingStreetSuffix,
                    city: mailingCity,
                    state: mailingState,
                    zip: mailingZip,
                    country: mailingCountry,
                  }}
                  onChange={addr => {
                    setMailingAddress1(addr.street1);
                    setMailingStreetSuffix(addr.streetSuffix ?? '');
                    setMailingCity(addr.city);
                    setMailingState(addr.state);
                    setMailingZip(addr.zip);
                    setMailingCountry(addr.country ?? '');
                  }}
                  showStreet2={false}
                  showStreetSuffix
                  streetSuffixOptions={streetSuffixOptions}
                  showCountry
                  statesOptions={FormData.states}
                  street1Label="Address"
                />
              </div>
            )}

            {/* Lead Client */}
            <div className="border-t mt-4 pt-4" style={{ borderColor: brand.colors.border }}>
              <FormCheckbox
                brand={brand}
                label="Lead Client"
                name="isLeadClient"
                checked={isLeadClient}
                onChange={e => setIsLeadClient(e.target.checked)}
                helpText="Billing section — select the client for this order"
              />
              {isLeadClient && (
                <div className="ml-6">
                  <FormInput
                    brand={brand}
                    label="Billing Code"
                    name="billingZip"
                    value={billingZip}
                    onChange={e => setBillingZip(e.target.value)}
                    required
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex justify-end gap-3 px-6 py-4 border-t"
          style={{ borderColor: brand.colors.border }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm border"
            style={{ borderColor: brand.colors.border, color: brand.colors.text }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid}
            className="px-4 py-2 rounded-lg text-sm"
            style={{
              backgroundColor: isValid ? brand.colors.primary : brand.colors.border,
              color: isValid ? brand.colors.onPrimary : brand.colors.textMuted,
              cursor: isValid ? 'pointer' : 'not-allowed',
            }}
          >
            {isEditing ? 'Update' : 'Add Party'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPartyModal;
